/**
 * The pure state transitions (web-plan.md F1) — every rule from the desktop's
 * `state/*.rs`, written as functions over mutable drafts (the runtime calls
 * them inside Immer's `produce`). Each carries its inherited rule in a
 * comment; none of them does IO.
 */
import type {
  Attachment,
  Call,
  ChannelState,
  DirectoryEntry,
  Entity,
  Expiring,
  Forwarded,
  FriendsSnapshot,
  Message,
  MyReaction,
  PresenceEntry,
  ReactionGroup,
  ReplyQuote,
  ReplyTo,
  SeqReactions,
  Service,
  UserStatus,
} from "../gen/dto";
import { SERVICE_KINDS } from "../gen/dto";

import type { Dict } from "../lib/i18n";
import {
  type Preview,
  cutPreview,
  plainPreview,
  prefixUnits,
  spoilersWithin,
} from "../lib/preview";
import {
  type Bubble,
  type ChannelCardEntry,
  type ChannelLinkView,
  type ClockState,
  type CommandsEntry,
  type DirectoryState,
  type FeedState,
  type ForwardDraft,
  type FriendsState,
  type Person,
  type PresenceState,
  type Room,
  type SidebarTab,
  CLOCK_SAMPLES,
  MAX_FORWARD,
  MAX_SELECTED,
  PENDING_SEQ,
  TYPING_EXPIRY_MS,
  TYPING_SEND_PERIOD_MS,
  type VoiceCall,
  type VoicePeer,
  WINDOW_SIZE,
  channelCardKey,
  isDmRoom,
  isPet,
} from "./types";

// ---------------------------------------------------------------------------
// Directory

/** The display-name ladder (D59): display_name → username → `#{id}`. One
 * function so every name-drawing site agrees. */
export function nameOf(dir: DirectoryState, userId: number): string {
  const p = dir.people[userId];
  if (!p) return `#${userId}`;
  return p.displayName && p.displayName !== "" ? p.displayName : p.username;
}

export function selfIdOf(dir: DirectoryState): number {
  return dir.selfId !== 0 ? dir.selfId : dir.selfIdFallback;
}

/** Id 0 is never anybody — the guard every call site repeats. */
export function isSelf(dir: DirectoryState, userId: number): boolean {
  const self = selfIdOf(dir);
  return self !== 0 && userId === self;
}

/** Everybody by id and handle — what a send finds `@username`s by
 * (dioxus-plan.md D284). The whole directory, not the open channel's roster
 * the composer's `@` list offers: a mention is a link to a person, a
 * hand-typed `@olya` means Olya whether or not the list was open, and whether
 * she can read the channel is its privacy's business (D114). */
export function handlesOf(dir: DirectoryState): [number, string][] {
  return Object.entries(dir.people).map(([id, p]) => [Number(id), p.username]);
}

/** Whether two spellings name one account: a username is one account whatever
 * its case (gateway/migrations/0035_username_fold.sql), and the spaces around
 * a typed one are not part of it. */
function sameUsername(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** A directory row out of its wire entry — the three keys only a pet carries
 * (pets-plan.md D349) included, and absent when the entry does not say them. */
function personOf(e: DirectoryEntry): Person {
  const p: Person = {
    username: e.username,
    displayName: e.display_name,
    bio: e.bio,
    avatarFileId: e.avatar_file_id,
    createdAt: e.created_at,
    bubbleLight: e.bubble_light,
    bubbleDark: e.bubble_dark,
  };
  if (e.kind !== undefined) p.kind = e.kind;
  if (e.pet_rev !== undefined) p.petRev = e.pet_rev;
  if (e.retired === true) p.retired = true;
  return p;
}

/** Whether a pet's card moved between two readings of its row: its revision,
 * or its retirement (D365), or its arrival — what a chat's `/` list read
 * before it is stale against (D360). */
function petMoved(before: Person | undefined, after: Person): boolean {
  if (!isPet(after)) return false;
  return (
    !isPet(before) ||
    before?.petRev !== after.petRev ||
    (before?.retired === true) !== (after.retired === true)
  );
}

/** A pet whose card moved: the revision it moved to, and whether it is
 * retired now (D365) — which takes it out of every `/` list whatever its
 * revision says. */
export interface PetMove {
  petId: number;
  rev: number;
  retired: boolean;
}

function moveOf(petId: number, pet: Person): PetMove {
  return { petId, rev: pet.petRev ?? 0, retired: pet.retired === true };
}

/** The whole directory, applied whole. Answers the pets whose card moved
 * since the last reading, for the `/` lists read against them (D360). */
export function applyDirectory(
  dir: DirectoryState,
  entries: DirectoryEntry[],
  selfName: string,
): PetMove[] {
  const moved: PetMove[] = [];
  for (const e of entries) {
    const next = personOf(e);
    if (petMoved(dir.people[e.user_id], next)) moved.push(moveOf(e.user_id, next));
    dir.people[e.user_id] = next;
    // Login swallows a failing /v1/me; the username match is the fallback
    // identity until the real one lands. Blind to case, the way the gateway
    // matches names: `selfName` is what was typed at the sign-in card, and
    // `аня` signs in to the account called `Аня`.
    if (dir.selfId === 0 && selfName !== "" && sameUsername(e.username, selfName)) {
      dir.selfIdFallback = e.user_id;
    }
  }
  return moved;
}

/** A whole DirectoryEntry, applied whole — a pet's `kind`, `pet_rev` and
 * `retired` with it, or the next frame about a pet would turn it back into a
 * person. Unknown id → no-op (no half-populated row), except for a pet: its
 * card is announced whole the moment it is made, and search finds it from
 * then on (D361). created_at 0 in an update is ignored (a rename must not
 * repaint the avatar). Answers the pet's move when its card moved. */
export function applyDirectoryUpdate(dir: DirectoryState, e: DirectoryEntry): PetMove | null {
  const p = dir.people[e.user_id];
  const next = personOf(e);
  const moved = petMoved(p, next) ? moveOf(e.user_id, next) : null;
  if (!p) {
    if (isPet(next) && e.created_at !== 0) dir.people[e.user_id] = next;
    return moved;
  }
  p.username = e.username;
  p.displayName = e.display_name;
  p.bio = e.bio;
  p.avatarFileId = e.avatar_file_id;
  if (e.created_at !== 0) p.createdAt = e.created_at;
  p.bubbleLight = e.bubble_light;
  p.bubbleDark = e.bubble_dark;
  // Written only when they differ: a person's row has none of the three, and
  // writing an absent key as `undefined` would still read to Immer as a change.
  if (p.kind !== next.kind) p.kind = next.kind;
  if (p.petRev !== next.petRev) p.petRev = next.petRev;
  if (p.retired !== next.retired) p.retired = next.retired;
  return moved;
}

/** The pet a direct conversation is with, or null — for a channel, a DM
 * with a person, or a peer the directory has not heard of yet. */
export function petPeerOf(room: Room, dir: DirectoryState): number | null {
  if (!isDmRoom(room) || room.peerId === null) return null;
  return isPet(dir.people[room.peerId]) ? room.peerId : null;
}

/** Whether a row belongs on a tab (D179, pets-plan.md D361): Friends holds the
 * conversations with people, Pets the ones with pets, Channels everything that
 * is not a conversation, and All — and any key this build does not know —
 * everything. A DM whose peer the directory has not heard of yet is a
 * person's: that is what it was before pets, and what a pet's row turns into
 * only when the directory says so. */
export function tabHolds(tab: SidebarTab, room: Room, dir: DirectoryState): boolean {
  switch (tab) {
    case "channels":
      return !isDmRoom(room);
    case "friends":
      return isDmRoom(room) && petPeerOf(room, dir) === null;
    case "pets":
      return petPeerOf(room, dir) !== null;
    default:
      return true;
  }
}

/** The chats whose `/` list a pet's move makes stale (D360): each list that
 * holds the pet's commands at an older revision — and each that holds none of
 * them, because a pet with no commands has no row to carry its revision and
 * may be in that chat all the same. A list that already has the pet at this
 * revision, or past it, stays — unless the pet was retired, which takes its
 * rows out of every list (D365) and puts none anywhere. A list still in the
 * air is asked again: its answer may be older than the move. */
export function staleCommandLists(
  commands: Record<number, CommandsEntry>,
  move: PetMove,
): number[] {
  const stale: number[] = [];
  for (const [key, entry] of Object.entries(commands)) {
    if (entry.t !== "ready") {
      stale.push(Number(key));
      continue;
    }
    const rows = entry.commands.filter((c) => c.pet_id === move.petId);
    const moved = move.retired
      ? rows.length > 0
      : rows.length === 0 || rows.some((c) => c.pet_rev < move.rev);
    if (moved) stale.push(Number(key));
  }
  return stale;
}

/** Stop and `/restart` (D368), from the frame or the send that took it back. */
export function roomsOnPetStopped(rooms: Room[], channelId: number, stopped: boolean): void {
  const row = roomOf(rooms, channelId);
  if (row) row.petStopped = stopped;
}

// ---------------------------------------------------------------------------
// Previews and stamps

const PREVIEW_UNITS = 80;
/** How much of the answered message's text a quote carries (D273): the wire's
 * `QUOTE_UNITS` (wire/src/dto.rs), for the quotes this client writes itself —
 * over its own reply while it is in flight, and over the replies to a message
 * whose edit it has just heard of. */
const QUOTE_UNITS = 128;

/** One function for sidebar rows AND reply quotes: service → text (cut at
 * 80 UTF-16 units, never through a surrogate pair, its spoilers still hidden —
 * D343) → attachment kind → "". */
export function preview(m: Message, t: Dict): Preview {
  return summary(m.service ?? null, m.text, m.entities ?? [], m.attachments ?? [], t);
}

/** The same line for the message a reply answers, from the quote the reply
 * carries of it (D273). The quote holds more of a long text than the line
 * shows, so the cut lands where it would have on the whole text — and its
 * spoilers, the one entity it carries (D343), are cut where the text was. */
export function quotePreview(q: ReplyQuote, t: Dict): Preview {
  return summary(q.service ?? null, q.text ?? "", q.entities ?? [], q.attachments ?? [], t);
}

/** The quote of `m` as it now stands (D273) — `ReplyQuote::of` on the Rust
 * side, which is what the gateway sends: who wrote it, the start of its text
 * and the spoilers over that start (D343), what it holds, and what kind of
 * notice it is if the server wrote it. */
export function quoteOf(m: Message): ReplyTo {
  const quote: ReplyQuote = { sender_id: m.sender_id };
  const text = prefixUnits(m.text, QUOTE_UNITS);
  if (text !== "") quote.text = text;
  const spoilers = spoilersWithin(m.entities ?? [], text.length);
  if (spoilers.length) quote.entities = spoilers;
  if (m.attachments?.length) quote.attachments = m.attachments;
  if (m.service) quote.service = m.service;
  return { state: "live", ...quote };
}

function summary(
  service: Service | null,
  text: string,
  entities: Entity[],
  attachments: Attachment[],
  t: Dict,
): Preview {
  if (service) {
    const kind = (service as { kind: string }).kind;
    if (!SERVICE_KINDS.includes(kind as (typeof SERVICE_KINDS)[number])) {
      return plainPreview(t.unsupportedMessage());
    }
    // Short, name-free forms: the row is narrow and the directory is not in
    // scope where this is built.
    return plainPreview(t.svcPreview(kind));
  }
  // Never split a surrogate pair: `cutPreview` steps back off a low half.
  if (text !== "") return cutPreview(text, entities, PREVIEW_UNITS);
  return plainPreview(attachmentSummary(attachments, t));
}

function attachmentSummary(attachments: Attachment[], t: Dict): string {
  const first = attachments[0];
  if (first) {
    switch (first.kind) {
      case "photo":
        return attachments.length > 1 ? `📷 ×${attachments.length}` : "📷";
      case "video":
        return "🎬";
      case "video_message":
        return "🎥";
      case "voice":
        return "🎤";
      case "file":
        return `📎 ${first.file_name ?? ""}`.trim();
      default:
        return t.unsupportedAttachment();
    }
  }
  return "";
}

export function sidebarStamp(atMs: number, nowMs: number, t: Dict): string {
  const at = new Date(atMs);
  const now = new Date(nowMs);
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  if (sameDay) {
    return at.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    at.getFullYear() === yesterday.getFullYear() &&
    at.getMonth() === yesterday.getMonth() &&
    at.getDate() === yesterday.getDate()
  ) {
    return t.yesterday();
  }
  return at.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ---------------------------------------------------------------------------
// Feed

export function newBubble(message: Message, senderName: string, isOwn: boolean): Bubble {
  return {
    uiKey: message.nonce
      ? `${message.channel_id}:${message.sender_id}:nonce:${message.nonce}`
      : `${message.channel_id}:seq:${message.seq}`,
    message,
    senderName,
    isOwn,
    showHeader: true,
    state: "sent",
    sendProgress: 1,
    localPreviews: [],
    replyAuthor: null,
    replyPreview: null,
    replyGone: false,
    forwardAuthor: null,
    reactions: [],
    myReaction: null,
  };
}

export function bubbleSeq(b: Bubble): number {
  return b.message.seq;
}

export function oldestSeq(feed: FeedState): number | undefined {
  return feed.bubbles[0]?.message.seq;
}

/** The ?after= cursor: newest bubble that is not the optimistic sentinel. */
export function newestSeq(feed: FeedState): number | undefined {
  for (let i = feed.bubbles.length - 1; i >= 0; i -= 1) {
    const seq = feed.bubbles[i]!.message.seq;
    if (seq !== PENDING_SEQ) return seq;
  }
  return undefined;
}

/** The newest CONFIRMED message — what the sidebar may quote once the one it
 * quoted is gone. Never an optimistic bubble: a send in flight is not
 * something the channel has said yet. */
export function newestMessage(feed: FeedState): Message | null {
  for (let i = feed.bubbles.length - 1; i >= 0; i -= 1) {
    const b = feed.bubbles[i]!;
    if (b.message.seq !== PENDING_SEQ) return b.message;
  }
  return null;
}

/** What a read mark may say: the newest REAL seq, and only when it moves the
 * mark forward. PENDING_SEQ posted once would poison the mark forever.
 *
 * A window that holds the channel's end says the CEILING instead (D277): a seq
 * between its newest bubble and `maxSeq` is a message that was deleted, and
 * whoever reached the end has read it as far as anybody can. Stopping at the
 * newest bubble left the mark short of `maxSeq` for good whenever the last
 * messages were deleted before they were read — nothing past the mark would
 * ever be on screen to move it, and the badge counted them until somebody
 * wrote something new. */
export function readableSeq(feed: FeedState): number | null {
  if (feed.reachedEnd) {
    const end = Math.max(feed.maxSeq, newestSeq(feed) ?? 0);
    return end > feed.lastReadSeq && end !== PENDING_SEQ ? end : null;
  }
  // The bottom is "everything", and the clamp below is what keeps PENDING_SEQ
  // out of it.
  return readableSeqUpTo(feed, Number.POSITIVE_INFINITY);
}

/** The same question for a reader who is NOT at the bottom: the mark earned by
 * messages passing under the eye, given everything up to `seen` has been on
 * screen (D249). Clamped to the newest real seq and not merely checked against
 * it — `seen` comes from a DOM id, and `POST /read` stores `max(existing, seq)`
 * without clamping, so a position this feed does not hold must not reach it.
 * `null` when the mark would not move, which is what keeps a scroll from
 * posting the same position on every event. */
export function readableSeqUpTo(feed: FeedState, seen: number): number | null {
  const newest = newestSeq(feed);
  if (newest === undefined) return null;
  const seq = Math.min(seen, newest);
  return seq > feed.lastReadSeq ? seq : null;
}

/** Derived, never accumulated — an accumulator was only ever reset.
 *
 * Counted off the window where the window can answer (D277): one that holds
 * the channel's end and starts no later than the first seq past the mark holds
 * every message past the mark there is, and a seq it lacks is a deletion — which
 * the subtraction went on counting as new for as long as the mark stood below. */
export function newBelow(feed: FeedState): number {
  const mark = feed.lastReadSeq;
  const oldest = oldestSeq(feed);
  const holdsAllPastMark =
    feed.reachedEnd && (feed.reachedStart || (oldest !== undefined && oldest <= mark + 1));
  if (holdsAllPastMark) {
    return feed.bubbles.filter((b) => b.message.seq !== PENDING_SEQ && b.message.seq > mark).length;
  }
  return Math.max(0, feed.maxSeq - mark);
}

/** Whether the window holds the channel's end once a page has landed (the
 * desktop's `end_holds`): it holds the newest message it has heard of, or the
 * page said the channel ends here — `saidSo` — and nothing arrived while it was
 * in flight (`askedAt` is the ceiling when it was asked for). The second way is
 * not the first because a deletion never lowers `maxSeq`: a channel whose last
 * messages are gone ends below its ceiling, and only a page can say so. */
export function endHolds(feed: FeedState, saidSo: boolean, askedAt: number): boolean {
  return (newestSeq(feed) ?? 0) >= feed.maxSeq || (saidSo && feed.maxSeq <= askedAt);
}

/** The ceiling as GET /v1/state has it after a reconnect. Below it the window
 * cannot tell a deleted message from a missed one, so it stops claiming the
 * end until the next page settles which (the desktop's `resync_ceiling`). */
export function resyncCeiling(feed: FeedState, maxSeq: number): void {
  feed.maxSeq = maxSeq;
  if ((newestSeq(feed) ?? 0) < maxSeq) feed.reachedEnd = false;
}

/** A live message moved the ceiling to `seq`. Past `maxSeq + 1` it skipped
 * seqs this window was never told about — missed, not deleted, as far as
 * anybody here knows — so the window stops claiming the end (D277). */
function raiseCeiling(feed: FeedState, seq: number): void {
  if (seq > feed.maxSeq + 1) feed.reachedEnd = false;
  if (seq > feed.maxSeq) feed.maxSeq = seq;
}

/** Full reset: everything but the four seeds goes back to defaults —
 * bubbles, divider, mode, typing map, the typing throttle, the flags. */
export function openFeed(
  feed: FeedState,
  channelId: number,
  name: string,
  maxSeq: number,
  lastReadSeq: number,
): void {
  feed.channelId = channelId;
  feed.channelName = name;
  feed.bubbles = [];
  feed.maxSeq = maxSeq;
  feed.lastReadSeq = lastReadSeq;
  feed.reachedStart = false;
  feed.reachedEnd = false;
  feed.loadingOlder = false;
  feed.loading = true;
  feed.dividerBefore = null;
  feed.mode = { t: "new" };
  feed.composerRevision += 1;
  // A selection is of one channel (forwarding.md D377).
  feed.selected = [];
  feed.error = null;
  feed.typing = {};
  feed.lastTypingSentMs = 0;
}

/** Seq-dedup in both directions; the original wins; prepend keeps the page's
 * ascending order at the head. Does NOT touch maxSeq or reply previews. */
export function appendMessages(feed: FeedState, page: Message[], atEnd: boolean): void {
  let index = 0;
  for (const m of page) {
    if (feed.bubbles.some((b) => b.message.seq === m.seq)) continue;
    const bubble = newBubble(m, "", false);
    if (atEnd) {
      feed.bubbles.push(bubble);
    } else {
      feed.bubbles.splice(index, 0, bubble);
      index += 1;
    }
  }
  regroup(feed);
}

/** After a prepend: drop the BOTTOM (the edge the reader scrolls away from),
 * and admit we no longer hold the end. */
export function trimTail(feed: FeedState): void {
  if (feed.bubbles.length > WINDOW_SIZE) {
    feed.bubbles.length = WINDOW_SIZE;
    feed.reachedEnd = false;
  }
}

/** After an append: drop the TOP and admit we no longer hold the start. */
export function trimHead(feed: FeedState): void {
  if (feed.bubbles.length > WINDOW_SIZE) {
    feed.bubbles.splice(0, feed.bubbles.length - WINDOW_SIZE);
    feed.reachedStart = false;
  }
}

/** The echo merge: (a) adopt by nonce — the bubble object and its flags
 * survive; (b) replace by seq — the edit path, chips survive; (c) insert in
 * seq order, before any optimistic bubble by construction. */
export function reconcile(feed: FeedState, server: Message, t: Dict): void {
  // Before anything else: every reply to this message quotes it (D273),
  // wherever the message itself is — an edit far up the history shows in the
  // header of the reply that is here.
  if (requote(feed, server)) fillReplyPreviews(feed, t);
  // …and a picked message is picked as it now stands (D377), wherever the
  // window is.
  const picked = feed.selected.findIndex((m) => m.seq === server.seq);
  if (picked !== -1) feed.selected[picked] = server;
  // (a) nonce match on a still-pending bubble.
  const pending = feed.bubbles.find(
    (b) => b.message.nonce !== "" && b.message.nonce === server.nonce && b.state !== "sent",
  );
  if (pending) {
    // A history page may beat the send/WS ACK. Keep the optimistic rendering
    // identity and collapse its already-visible server copy with its chips.
    const duplicate = feed.bubbles.find((b) => b !== pending && b.message.seq === server.seq);
    if (duplicate) {
      pending.reactions = duplicate.reactions;
      pending.myReaction = duplicate.myReaction;
      feed.bubbles.splice(feed.bubbles.indexOf(duplicate), 1);
    }
    pending.message = server;
    pending.state = "sent";
    pending.sendProgress = 1;
    raiseCeiling(feed, server.seq);
    // Other messages can arrive while an upload is pending; the ACK restores
    // sequence order without changing the bubble's uiKey.
    feed.bubbles.sort((a, b) => a.message.seq - b.message.seq);
    regroup(feed);
    fillReplyPreviews(feed, t);
    return;
  }
  // (b) seq match.
  const existing = feed.bubbles.find((b) => b.message.seq === server.seq);
  if (existing) {
    existing.message = server;
    regroup(feed);
    fillReplyPreviews(feed, t);
    return;
  }
  // (c) insert.
  raiseCeiling(feed, server.seq);
  const at = feed.bubbles.findIndex((b) => b.message.seq > server.seq);
  feed.bubbles.splice(at === -1 ? feed.bubbles.length : at, 0, newBubble(server, "", false));
  trimHead(feed);
  regroup(feed);
}

/** The row leaves the window (D253): a deleted message is a hole in the seq
 * run, and every cursor here reads a hole exactly as it reads a gap. */
export function applyDeletion(feed: FeedState, seq: number, t: Dict): void {
  // Every reply to it carries a quote of the words just withdrawn (D273). It
  // becomes the word that they are gone — before asking whether the window
  // holds the message, because a reply quotes its target wherever it is.
  let quoted = false;
  for (const b of feed.bubbles) {
    if (b.message.reply_to_seq === seq) {
      b.message.reply_to = { state: "deleted" };
      quoted = true;
    }
  }
  if (quoted) fillReplyPreviews(feed, t);
  // A message that is gone is not forwarded (D377), and the bar's count says
  // so — whether or not the window still holds it.
  if (feed.selected.some((m) => m.seq === seq)) {
    feed.selected = feed.selected.filter((m) => m.seq !== seq);
  }
  const at = feed.bubbles.findIndex((x) => x.message.seq === seq);
  if (at === -1) return;
  feed.bubbles.splice(at, 1);
  // The unread line hung off this message; left pointing at a bubble that is
  // gone it would simply stop being drawn.
  if (feed.dividerBefore === seq) insertUnreadDivider(feed);
  // The bubble above the hole may now start a run.
  regroup(feed);
}

/** Grouping: header when the sender changes — never on a pause, however long:
 * one person's consecutive messages are one block with one name and face (the
 * desktop's `Feed::regroup`). A service line shares a header with nothing and
 * breaks the run after it; the divider breaks runs; the date line is the
 * feed's own cut (`buildGroups`), since the day is the reader's local one. */
export function regroup(feed: FeedState): void {
  const divider = feed.dividerBefore;
  let prev: number | null = null;
  for (const b of feed.bubbles) {
    if (divider === b.message.seq) prev = null;
    if (b.message.service != null) {
      b.showHeader = true;
      prev = null;
      continue;
    }
    b.showHeader = prev !== b.message.sender_id;
    prev = b.message.sender_id;
  }
}

export function insertUnreadDivider(feed: FeedState): void {
  feed.dividerBefore = null;
  if (feed.lastReadSeq === 0 || feed.lastReadSeq >= feed.maxSeq) return;
  feed.dividerBefore =
    feed.bubbles.find((b) => b.message.seq > feed.lastReadSeq)?.message.seq ?? null;
  regroup(feed);
}

/** Once dismissed it stays gone for the life of the open channel. Returns
 * whether anything changed, so the caller can stay quiet on the scroll spam. */
export function dismissUnreadDivider(feed: FeedState): boolean {
  if (feed.dividerBefore === null) return false;
  feed.dividerBefore = null;
  regroup(feed);
  return true;
}

/** Resolve names + own-ness over the window; ends in fillReplyPreviews. */
export function resolveNames(feed: FeedState, dir: DirectoryState, t: Dict): void {
  const self = selfIdOf(dir);
  for (const b of feed.bubbles) {
    b.senderName = nameOf(dir, b.message.sender_id);
    b.isOwn = self !== 0 && b.message.sender_id === self;
    // Whoever wrote what a reply answers is a name like these (D273).
    const quote = b.message.reply_to_seq != null ? b.message.reply_to : null;
    b.replyAuthor = quote?.state === "live" ? nameOf(dir, quote.sender_id) : null;
    // …and so is whoever wrote a forward's words (D376). A header this build
    // could not read names nobody and keeps its line all the same.
    const forwarded = forwardedOf(b.message);
    b.forwardAuthor =
      forwarded !== null && forwarded.sender_id !== 0 ? nameOf(dir, forwarded.sender_id) : null;
  }
  fillReplyPreviews(feed, t);
}

// ---------------------------------------------------------------------------
// Forwarding (docs/forwarding.md D374–D378)

/** A forward's header as this build can read it (D376): null on a message its
 * sender wrote, and one naming nobody (`sender_id` 0) for a header shaped past
 * what this build knows — what the Rust reader's `lossy_forwarded` makes of
 * it, here by hand, since nothing on this side checks a body against its
 * type. */
export function forwardedOf(m: Message): Forwarded | null {
  const raw: unknown = (m as { forwarded?: unknown }).forwarded;
  if (raw === undefined || raw === null) return null;
  const header = typeof raw === "object" ? (raw as Partial<Record<keyof Forwarded, unknown>>) : {};
  if (typeof header.sender_id !== "number") return { sender_id: 0, sent_at_ms: 0 };
  return {
    sender_id: header.sender_id,
    sent_at_ms: typeof header.sent_at_ms === "number" ? header.sent_at_ms : 0,
  };
}

/** `Forwarded::of`: the header a copy of `m` carries (D374) — the one `m`
 * already has when it is itself a forward, so the line always names whoever
 * wrote the words; its own author and time otherwise. */
export function forwardHeaderOf(m: Message): Forwarded {
  return forwardedOf(m) ?? { sender_id: m.sender_id, sent_at_ms: m.sent_at_ms };
}

/** `Message::forwardable` (D375): not a line the server wrote, and not words
 * their sender gave a lifetime. */
export function forwardable(m: Message): boolean {
  return m.service == null && m.ttl_s == null;
}

/** Whether a message may be picked (docs/message-selection.md D379): one the
 * server has numbered and did not write itself. A lifetime does not keep it
 * out — such a message cannot be forwarded, but it can be copied and deleted. */
export function selectable(m: Message): boolean {
  return m.seq !== PENDING_SEQ && m.service == null;
}

/** Picks the message at `seq`, or puts it back (D377, D379). Only a message
 * the window holds that may be picked at all is taken; nothing past
 * MAX_SELECTED. Returns whether the selection moved. */
export function toggleSelected(feed: FeedState, seq: number): boolean {
  const at = feed.selected.findIndex((m) => m.seq === seq);
  if (at !== -1) {
    feed.selected.splice(at, 1);
    return true;
  }
  if (feed.selected.length >= MAX_SELECTED || seq === PENDING_SEQ) return false;
  const bubble = feed.bubbles.find((b) => b.message.seq === seq);
  if (!bubble || !selectable(bubble.message)) return false;
  const after = feed.selected.findIndex((m) => m.seq > seq);
  feed.selected.splice(after === -1 ? feed.selected.length : after, 0, bubble.message);
  return true;
}

/** What the selection's bar offers (D379): each action only where it applies
 * to every pick — Telegram's rule, since a button that silently left some of
 * them behind would be worse than none. `moderates`: the reader administers
 * this chat, its own admin or the server's — the gateway's rule for deleting
 * somebody else's message (chat-api.md §2.4). */
export interface SelectionActions {
  forward: boolean;
  delete: boolean;
  copy: boolean;
}

export function selectionActions(
  picked: readonly Message[],
  selfId: number,
  moderates: boolean,
): SelectionActions {
  if (picked.length === 0) return { forward: false, delete: false, copy: false };
  return {
    forward: picked.every(forwardable),
    delete: moderates || picked.every((m) => selfId !== 0 && m.sender_id === selfId),
    copy: picked.some((m) => m.text !== ""),
  };
}

/** `dd.MM.yyyy HH:mm` in the reader's own zone — the head over each message
 * of a copied selection (D381), day first in both languages, as the desktop's
 * `time::clipboard_stamp`. */
export function clipboardStamp(ms: number): string {
  const at = new Date(ms);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(at.getDate())}.${two(at.getMonth() + 1)}.${at.getFullYear()} ${two(at.getHours())}:${two(at.getMinutes())}`;
}

/** The picked messages as text for the clipboard (D381), the desktop's
 * `selection_text` line for line: only words travel; one message with words
 * is its words alone; several are a transcript — `Name, [dd.MM.yyyy HH:mm]`
 * over each one's words, a blank line between, a forward's words under its
 * own line — in the order said. Null when nothing picked has words. */
export function selectionText(
  picked: readonly Message[],
  nameOf: (id: number) => string,
  t: Dict,
): string | null {
  const worded = [...picked].filter((m) => m.text !== "").sort((a, b) => a.seq - b.seq);
  if (worded.length === 0) return null;
  if (worded.length === 1) return worded[0]!.text;
  return worded
    .map((m) => {
      let block = `${nameOf(m.sender_id)}, [${clipboardStamp(m.sent_at_ms)}]\n`;
      // A header this build could not read names nobody, as the bubble's own
      // line does (D376).
      const header = forwardedOf(m);
      if (header !== null) {
        block +=
          header.sender_id === 0
            ? `${t.forwardedUnknown()}\n`
            : `${t.forwardedFrom()} ${nameOf(header.sender_id)}\n`;
      }
      return block + m.text;
    })
    .join("\n\n");
}

/** What forwarding `seqs` of this window sends (D377): each message as it was
 * picked when it was picked, as the window holds it otherwise — only the ones
 * that may go, ascending by seq, each once, at most MAX_FORWARD. */
export function forwardableOf(feed: FeedState, seqs: number[]): Message[] {
  const out: Message[] = [];
  for (const seq of [...new Set(seqs)].sort((a, b) => a - b)) {
    if (seq === PENDING_SEQ) continue;
    const m =
      feed.selected.find((x) => x.seq === seq) ??
      feed.bubbles.find((b) => b.message.seq === seq)?.message;
    if (m && forwardable(m)) out.push(m);
  }
  return out.slice(0, MAX_FORWARD);
}

/** Whose words a forward carries, each once, in the order they first come —
 * what the bar's first line names (D377). */
export function draftAuthors(draft: ForwardDraft): number[] {
  const out: number[] = [];
  for (const m of draft.messages) {
    const author = forwardHeaderOf(m).sender_id;
    if (!out.includes(author)) out.push(author);
  }
  return out;
}

/** The waiting forward less a message of `channelId` that is gone (D377): the
 * gateway would refuse to copy it, and the bar must not count it. Null once
 * nothing is left — the bar comes down with its last message — and the same
 * draft when the deletion is nothing of its. */
export function forwardWithout(
  draft: ForwardDraft | null,
  channelId: number,
  seq: number,
): ForwardDraft | null {
  if (draft === null || draft.from !== channelId) return draft;
  if (!draft.messages.some((m) => m.seq === seq)) return draft;
  const messages = draft.messages.filter((m) => m.seq !== seq);
  return messages.length > 0 ? { ...draft, messages } : null;
}

/** The words of the header over every reply (D273), from the quote each reply
 * carries — the gateway's, kept current by requote() and applyDeletion(). The
 * target does not have to be in the window. A quote that says the target was
 * deleted is `replyGone`; a reply carrying nothing readable about its target
 * keeps a header without words. Neither has an author: the name is
 * resolveNames()'s, and it goes here with the words. */
export function fillReplyPreviews(feed: FeedState, t: Dict): void {
  for (const b of feed.bubbles) {
    const quote = b.message.reply_to_seq != null ? b.message.reply_to : null;
    const live = quote?.state === "live" ? quote : null;
    b.replyPreview = live ? quotePreview(live, t) : null;
    b.replyGone = quote?.state === "deleted";
    if (!live) b.replyAuthor = null;
  }
}

/** Writes the quote of `target` as it now stands over every reply to it in the
 * window (D273): its edit, off the socket or out of our own PATCH's answer.
 * Nobody sends the replies again — each client requotes from the edited
 * message's own frame. Returns whether any reply in the window quotes it. */
export function requote(feed: FeedState, target: Message): boolean {
  let quoted = false;
  for (const b of feed.bubbles) {
    if (b.message.reply_to_seq === target.seq) {
      b.message.reply_to = quoteOf(target);
      quoted = true;
    }
  }
  return quoted;
}

/** The page's sibling reaction keys, applied over [lo, hi] — INCLUDING to
 * empty: a chip somebody cleared while we were away is exactly the stale
 * decoration being healed (D160). */
export function applyPageReactions(
  feed: FeedState,
  span: [number, number],
  rows: SeqReactions[],
  mine: MyReaction[],
): void {
  const [lo, hi] = span;
  for (const b of feed.bubbles) {
    const seq = b.message.seq;
    if (seq < lo || seq > hi) continue;
    b.reactions = rows
      .filter((r) => r.seq === seq)
      .map((r) => ({ emoji: r.emoji, count: r.count, users: r.users }));
    b.myReaction = mine.find((m) => m.seq === seq)?.emoji ?? null;
  }
}

export function applyReactions(feed: FeedState, seq: number, groups: ReactionGroup[]): void {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (!b) return;
  b.reactions = groups;
}

/** The reaction event: whole summary, never a delta — applying twice == once.
 * The actor being us (a second device) is how a chip becomes "mine". */
export function onReactionEvent(
  feed: FeedState,
  seq: number,
  userId: number,
  emoji: string | null,
  groups: ReactionGroup[],
  selfId: number,
): void {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (!b) return;
  b.reactions = groups;
  if (selfId !== 0 && userId === selfId) b.myReaction = emoji;
}

/** The optimistic mutator — remove-us-everywhere then add-us-once, never a
 * toggle, so the socket echo of our own change is a no-op. Returns the
 * previous value for rollback THROUGH THIS SAME FUNCTION. */
export function setMyReaction(
  feed: FeedState,
  seq: number,
  emoji: string | null,
  selfId: number,
): string | null {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (!b) return null;
  const previous = b.myReaction;
  b.myReaction = null;
  for (const g of b.reactions) {
    if (previous === g.emoji) g.count = Math.max(0, g.count - 1);
    g.users = g.users.filter((u) => u !== selfId);
  }
  b.reactions = b.reactions.filter((g) => g.count > 0);
  if (emoji !== null) {
    const g = b.reactions.find((x) => x.emoji === emoji);
    if (g) {
      g.count += 1;
      if (g.users.length < 3 && !g.users.includes(selfId)) g.users.push(selfId);
    } else {
      b.reactions.push({ emoji, count: 1, users: [selfId] });
    }
    b.myReaction = emoji;
  }
  return previous;
}

// Typing

export function shouldSendTyping(feed: FeedState, nowMs: number): boolean {
  if (nowMs - feed.lastTypingSentMs < TYPING_SEND_PERIOD_MS) return false;
  feed.lastTypingSentMs = nowMs;
  return true;
}

export function onRemoteTyping(
  feed: FeedState,
  userId: number,
  selfId: number,
  nowMs: number,
): void {
  if (selfId !== 0 && userId === selfId) return;
  feed.typing[userId] = nowMs;
}

/** Returns whether the visible set changed — the 2 s tick must not re-clone
 * the window when nothing moved. */
export function tickTyping(feed: FeedState, nowMs: number): boolean {
  let changed = false;
  for (const key of Object.keys(feed.typing)) {
    const id = Number(key);
    if (nowMs - feed.typing[id]! >= TYPING_EXPIRY_MS) {
      delete feed.typing[id];
      changed = true;
    }
  }
  return changed;
}

/** Ascending user_id — the wording must not depend on hash order. */
export function typingUserIds(feed: FeedState): number[] {
  return Object.keys(feed.typing)
    .map(Number)
    .sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Rooms

export function blankRoom(channelId: number, name: string): Room {
  return {
    channelId,
    ownerId: 0,
    kind: "standard",
    peerId: null,
    name,
    description: null,
    avatarFileId: null,
    preview: plainPreview(""),
    previewSeq: 0,
    previewExpiresAtMs: null,
    timeText: "",
    unread: 0,
    maxSeq: 0,
    lastReadSeq: 0,
    holes: [],
    lastMessageAtMs: 0,
    callMembers: [],
    callMemberIds: [],
    member: false,
    admin: false,
    settings: defaultSettings(),
    members: 0,
    joining: false,
    petStopped: false,
  };
}

export function defaultSettings(): import("../gen/dto").ChannelSettings {
  return {
    is_private: false,
    max_voice_ms: 600_000,
    max_media_bytes: 104_857_600,
    max_message_chars: 4096,
    slowmode_s: 0,
    voice_enabled: true,
    channel_scheme_only: false,
    bg_blur: 0,
    bg_brightness: 100,
  };
}

export function roomOf(rooms: Room[], channelId: number): Room | undefined {
  return rooms.find((r) => r.channelId === channelId);
}

/** THE unread formula — one owner, clamped at zero (u64 wrapped once). Less
 * the holes seen opening past the mark (D277), which the server's subtraction
 * still counts; one above this row's ceiling — the deletion of a message whose
 * own frame never came — waits until the ceiling passes it, because until
 * then it was never counted. The mark forgets what it has passed. */
export function refreshUnread(room: Room): void {
  const mark = room.lastReadSeq;
  // Reassigned only when something goes: a fresh array on every refresh would
  // read to Immer as a changed row on every message in every channel.
  if (room.holes.some((seq) => seq <= mark)) room.holes = room.holes.filter((seq) => seq > mark);
  const holes = room.holes.filter((seq) => seq <= room.maxSeq).length;
  room.unread = Math.max(0, room.maxSeq - mark - holes);
}

/** The line and its stamp, from the message they quote — written together, so
 * a line can never quote one message under another's time. And its deadline
 * with them (D298), so a line can never outlive the message it quotes. */
function quote(room: Room, m: Message, nowMs: number, t: Dict): void {
  room.preview = preview(m, t);
  room.previewSeq = m.seq;
  room.previewExpiresAtMs = m.expires_at_ms ?? null;
  room.timeText = sidebarStamp(m.sent_at_ms, nowMs, t);
  room.lastMessageAtMs = m.sent_at_ms;
}

/** Both halves together, always — "2 on the header over a panel saying 0"
 * was a real bug. */
export function setRoomCall(room: Room, participants: number[], dir: DirectoryState): void {
  room.callMemberIds = [...participants];
  room.callMembers = participants.map((id) => nameOf(dir, id));
}

/** unread first, then recency, then name (case-folded). Stable. */
export function resort(rooms: Room[]): void {
  rooms.sort(
    (a, b) =>
      Number(b.unread > 0) - Number(a.unread > 0) ||
      b.lastMessageAtMs - a.lastMessageAtMs ||
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );
}

/** Idempotent, and the reconnect path: rows are patched from the envelope,
 * unlisted rows are dropped, the row-level door pair is written AFTER the
 * settings object (it is public and carries for non-members too). */
export function applyState(
  rooms: Room[],
  channels: ChannelState[],
  dir: DirectoryState,
  nowMs: number,
  t: Dict,
): void {
  for (const s of channels) {
    let row = roomOf(rooms, s.channel_id);
    if (!row) {
      row = blankRoom(s.channel_id, "");
      rooms.push(row);
    }
    row.name = s.name;
    row.ownerId = s.owner_id;
    row.kind = s.kind ?? "standard";
    row.peerId = s.peer_id ?? null;
    row.description = s.description ?? null;
    row.avatarFileId = s.avatar_file_id ?? null;
    row.maxSeq = s.max_seq;
    row.lastReadSeq = s.last_read_seq;
    row.member = s.member;
    row.admin = s.admin;
    row.settings = s.settings ?? defaultSettings();
    row.settings.is_private = s.is_private;
    row.members = s.members;
    // Present only while stopped (D368): absent is "not stopped", which is
    // also what a gateway without pets means by saying nothing.
    row.petStopped = s.pet_stopped === true;
    refreshUnread(row);
    if (s.last_message) {
      quote(row, s.last_message, nowMs, t);
    } else {
      // Every message is gone: what a cold start draws, which the reconnect
      // path has to arrive at too.
      row.preview = plainPreview("");
      row.previewSeq = 0;
      row.previewExpiresAtMs = null;
      row.timeText = "";
      row.lastMessageAtMs = 0;
    }
    setRoomCall(row, s.call?.participants ?? [], dir);
  }
  for (let i = rooms.length - 1; i >= 0; i -= 1) {
    if (!channels.some((s) => s.channel_id === rooms[i]!.channelId)) rooms.splice(i, 1);
  }
  resort(rooms);
}

/** The message arm of the sidebar. Own message bumps the MARK (not skips the
 * recompute); someone else's in the open channel bumps NEITHER — unread
 * keeps its value while max_seq rises. */
export function roomsOnMessage(
  rooms: Room[],
  m: Message,
  openChannel: number | null,
  own: boolean,
  nowMs: number,
  t: Dict,
): void {
  const row = roomOf(rooms, m.channel_id);
  if (!row) return;
  row.maxSeq = Math.max(row.maxSeq, m.seq);
  // The line quotes the newest message; two frames can swap on the way.
  if (m.seq >= row.previewSeq) quote(row, m, nowMs, t);
  if (own) {
    row.lastReadSeq = Math.max(row.lastReadSeq, m.seq);
    refreshUnread(row);
  } else if (m.channel_id !== openChannel) {
    refreshUnread(row);
  }
  resort(rooms);
}

/** Preview changes only when the edited message IS the one the line quotes.
 * No resort. */
export function roomsOnEdited(rooms: Room[], m: Message, t: Dict): void {
  const row = roomOf(rooms, m.channel_id);
  if (!row) return;
  if (row.previewSeq === m.seq) row.preview = preview(m, t);
}

/** The same for a deletion (D253): the row is a copy of one message's words,
 * so deleting that message and leaving the row alone keeps them legible in the
 * sidebar after they have gone from the chat. Matched on `previewSeq`, not
 * `maxSeq`, which stops naming the quoted message once the newest one goes —
 * four messages deleted from the bottom up left the second one's words on the
 * row. `replacement` is the message that is newest now — only the open channel
 * can answer that, and without one the line and its stamp go blank until the
 * next GET /v1/state (the row keeps its place in the order).
 *
 * And a deleted message past the read mark is not unread (D277): it comes off
 * the badge of a row that is not open. The open one's badge does not move on
 * traffic (roomsOnMessage); the hole counts at the next read mark. Once only —
 * our own deletion is folded in on the answer and again on the frame, and a
 * disappearing message at its deadline by our own clock and again on the
 * gateway's `message_deleted` (D298). */
export function roomsOnDeleted(
  rooms: Room[],
  channelId: number,
  seq: number,
  replacement: Message | null,
  openChannel: number | null,
  nowMs: number,
  t: Dict,
): void {
  const row = roomOf(rooms, channelId);
  if (!row) return;
  if (row.previewSeq === seq) {
    if (replacement) {
      quote(row, replacement, nowMs, t);
    } else {
      row.preview = plainPreview("");
      row.previewSeq = 0;
      row.previewExpiresAtMs = null;
      row.timeText = "";
    }
  }
  if (seq > row.lastReadSeq && !row.holes.includes(seq)) {
    row.holes.push(seq);
    if (channelId !== openChannel) refreshUnread(row);
  }
  resort(rooms);
}

/** Monotonic: a stale frame cannot un-read a channel. Returns the row, or
 * undefined for an unknown channel (then nothing changed). */
export function roomsOnRead(rooms: Room[], channelId: number, effective: number): Room | undefined {
  const row = roomOf(rooms, channelId);
  if (!row) return undefined;
  row.lastReadSeq = Math.max(row.lastReadSeq, effective);
  refreshUnread(row);
  resort(rooms);
  return row;
}

/** A repeat channel_created is ignored whole — the name is NOT updated. */
export function roomsOnCreated(
  rooms: Room[],
  channelId: number,
  name: string,
  kind: string,
  peerId: number | null,
  createdBy: number,
  selfId: number,
): boolean {
  if (roomOf(rooms, channelId)) return false;
  const row = blankRoom(channelId, name);
  row.kind = kind;
  row.peerId = peerId;
  if (kind === "dm") {
    row.member = true;
    row.members = 2;
  } else if (createdBy !== 0) {
    row.members = 1;
    row.member = selfId !== 0 && createdBy === selfId;
    row.admin = row.member;
  }
  rooms.push(row);
  resort(rooms);
  return isDmRoom(row) || (createdBy !== 0 && createdBy === selfId);
}

/** The whole profile travels, never a delta: description null means cleared.
 * kind/peer_id thread through so a DM's rename cannot turn it into an
 * ordinary channel named after somebody. */
export function roomsOnUpdated(
  rooms: Room[],
  e: {
    channel_id: number;
    owner_id: number;
    name: string;
    kind?: string;
    peer_id?: number | null;
    description: string | null;
    avatar_file_id: string | null;
    is_private: boolean;
  },
): void {
  const row = roomOf(rooms, e.channel_id);
  if (!row) return;
  row.name = e.name;
  row.ownerId = e.owner_id;
  row.kind = e.kind ?? "standard";
  row.peerId = e.peer_id ?? null;
  row.description = e.description;
  row.avatarFileId = e.avatar_file_id;
  row.settings.is_private = e.is_private;
  resort(rooms);
}

/** Membership: `counted` makes this commutative with onJoined in either
 * order — one join, one head. Returns whether it was about us. */
export function roomsOnMembership(
  rooms: Room[],
  channelId: number,
  userId: number,
  change: string,
  admin: boolean,
  selfId: number,
): boolean {
  const row = roomOf(rooms, channelId);
  if (!row) return false;
  const mine = selfId !== 0 && userId === selfId;
  const counted = mine && row.member;
  const stays = change !== "left";
  if (mine) {
    row.member = stays;
    row.admin = stays && admin;
    row.joining = false;
  }
  if (change === "joined") {
    if (!counted) row.members += 1;
  } else if (change === "left") {
    row.members = Math.max(0, row.members - 1);
  }
  return mine;
}

/** Folds what POST /join itself answered — never wait for the broadcast,
 * which only comes when the join changed something (the creator's row is
 * exactly the case that would wait forever). */
export function roomsOnJoined(rooms: Room[], channelId: number, admin: boolean): void {
  const row = roomOf(rooms, channelId);
  if (!row) return;
  row.joining = false;
  if (!row.member) row.members += 1;
  row.member = true;
  row.admin = admin;
}

/** The card under a link to a channel (D279): the reader's own row when they
 * hold one — every public channel and every channel they are in — and the
 * gateway's card (D281) for one they do not, once it has answered; `null`
 * until then, and for a channel with no card at all. A direct conversation
 * wears its peer's face, as its sidebar row does. */
export function channelLinkView(
  rooms: Room[],
  cards: Record<string, ChannelCardEntry>,
  dir: DirectoryState,
  channelId: number,
  token: string | null,
): ChannelLinkView | null {
  const row = roomOf(rooms, channelId);
  if (row) {
    const peer = isDmRoom(row) && row.peerId !== null ? dir.people[row.peerId] : undefined;
    return {
      held: true,
      kind: row.kind,
      name: row.name,
      description: row.description,
      avatarFileId: peer?.avatarFileId ?? row.avatarFileId,
      faceCreatedAt: peer?.createdAt ?? 0,
      members: row.members,
      member: row.member,
      isPrivate: row.settings.is_private,
      joining: row.joining,
    };
  }
  const entry = cards[channelCardKey(channelId, token)];
  if (entry?.t !== "ready") return null;
  const card = entry.card;
  return {
    held: false,
    kind: card.kind ?? "standard",
    name: card.name,
    description: card.description ?? null,
    avatarFileId: card.avatar_file_id ?? null,
    faceCreatedAt: 0,
    members: card.members,
    member: card.member,
    isPrivate: card.is_private,
    joining: false,
  };
}

// ---------------------------------------------------------------------------
// Disappearing messages (docs/disappearing-messages.md D298)

/** One more reading of the gateway's clock, against this machine's as it
 * arrived: the `sent_at_ms` of a message handed over the moment it was stored
 * — a live frame, the answer to a send — or the `server_time_ms` of a
 * `GET /v1/state` answer. Never a history page, and never an edit's frame:
 * their `sent_at_ms` is as old as the message. The offset is the largest of
 * the newest `CLOCK_SAMPLES`, the reading the network delayed least. */
export function noteClockSample(clock: ClockState, serverMs: number, nowMs: number): void {
  const sample = serverMs - nowMs;
  if (!Number.isFinite(sample)) return;
  clock.samples.push(sample);
  if (clock.samples.length > CLOCK_SAMPLES) {
    clock.samples.splice(0, clock.samples.length - CLOCK_SAMPLES);
  }
  clock.offsetMs = Math.max(...clock.samples);
}

/** The gateway's now, as near as this client can tell — the clock every
 * `expires_at_ms` is written on. */
export function serverNow(clock: ClockState, nowMs: number): number {
  return nowMs + clock.offsetMs;
}

/** A copy of a message with a deadline that this client is showing. */
export interface TimedMessage {
  channelId: number;
  seq: number;
  expiresAtMs: number;
}

/** Every such copy there is: the open feed's bubbles, and the line of each
 * sidebar row — a row quotes its newest message's words whether or not its
 * channel is open. Only what the gateway has confirmed: an optimistic bubble's
 * deadline is this client's guess at one, and a send still on its way, or one
 * that failed, is not a message yet — it has nothing to disappear from. */
export function timedMessages(feed: FeedState, rooms: Room[]): TimedMessage[] {
  const timed: TimedMessage[] = [];
  for (const b of feed.bubbles) {
    const { seq, expires_at_ms: at } = b.message;
    // `isFinite` and not `!= null`: the JSON boundary is not shape-checked, and
    // a deadline that is not a number must read as none — never as one that is
    // always due, or never reached, for the timer to spin on.
    if (seq === PENDING_SEQ || !Number.isFinite(at)) continue;
    // Under the window's own channel, which is where the deletion looks for it.
    timed.push({ channelId: feed.channelId, seq, expiresAtMs: at as number });
  }
  for (const row of rooms) {
    const at = row.previewExpiresAtMs;
    if (row.previewSeq === 0 || !Number.isFinite(at)) continue;
    const held = timed.some((m) => m.channelId === row.channelId && m.seq === row.previewSeq);
    if (!held) {
      timed.push({ channelId: row.channelId, seq: row.previewSeq, expiresAtMs: at as number });
    }
  }
  return timed;
}

/** Writes the deadlines the gateway armed (D302) onto every copy of those
 * messages this client holds — the open feed's bubble and the sidebar row that
 * quotes it. Answers whether anything moved.
 *
 * Only ever onto a copy that has none: the gateway arms once and says so once,
 * but a socket that reconnects can replay a page over a frame, and «the first
 * reader started it» must not be rewritten by a second announcement. */
export function armExpiry(
  feed: FeedState,
  rooms: Room[],
  channelId: number,
  armed: readonly Expiring[],
): boolean {
  let moved = false;
  for (const e of armed) {
    if (!Number.isFinite(e.expires_at_ms)) continue;
    if (feed.channelId === channelId) {
      const b = feed.bubbles.find((b) => b.message.seq === e.seq);
      if (b && b.message.expires_at_ms == null) {
        b.message.expires_at_ms = e.expires_at_ms;
        moved = true;
      }
    }
    const row = rooms.find((r) => r.channelId === channelId);
    if (row && row.previewSeq === e.seq && row.previewExpiresAtMs == null) {
      row.previewExpiresAtMs = e.expires_at_ms;
      moved = true;
    }
  }
  return moved;
}

/** The copies past their deadline at `serverNowMs` — `Message::expired_at` on
 * the Rust side, at the deadline and not only after it. */
export function dueMessages(feed: FeedState, rooms: Room[], serverNowMs: number): TimedMessage[] {
  return timedMessages(feed, rooms).filter((m) => m.expiresAtMs <= serverNowMs);
}

/** The nearest deadline among them, on the gateway's clock, or null when
 * nothing held is going anywhere. */
export function nextDeadline(feed: FeedState, rooms: Room[]): number | null {
  let nearest: number | null = null;
  for (const m of timedMessages(feed, rooms)) {
    if (nearest === null || m.expiresAtMs < nearest) nearest = m.expiresAtMs;
  }
  return nearest;
}

// ---------------------------------------------------------------------------
// Friends

export type FriendNews =
  | { t: "requested"; userId: number }
  | {
      t: "accepted";
      userId: number;
    };

/** The snapshot writer (D174): one object, applied whole; the FIRST snapshot
 * seeds silently; the diff is computed against what we held, not claimed by
 * the frame. */
export function applyFriendsSnapshot(
  friends: FriendsState,
  snapshot: FriendsSnapshot,
): FriendNews[] {
  const news: FriendNews[] = [];
  if (friends.seeded) {
    for (const inc of snapshot.incoming) {
      if (!friends.incoming.some((r) => r.user_id === inc.user_id)) {
        news.push({ t: "requested", userId: inc.user_id });
      }
    }
    for (const f of snapshot.friends) {
      const wasFriend = friends.friends.some((x) => x.user_id === f.user_id);
      const wasOutgoing = friends.outgoing.includes(f.user_id);
      // Accepting one yourself is not news — only a request WE sent that
      // became a friendship is.
      if (!wasFriend && wasOutgoing) news.push({ t: "accepted", userId: f.user_id });
    }
  }
  friends.friends = snapshot.friends;
  friends.incoming = snapshot.incoming;
  friends.outgoing = snapshot.outgoing;
  friends.blocked = snapshot.blocked;
  friends.policy = snapshot.policy;
  friends.seeded = true;
  return news;
}

export function isFriend(friends: FriendsState, userId: number): boolean {
  return friends.friends.some((f) => f.user_id === userId);
}

// ---------------------------------------------------------------------------
// Presence

/** A transition frame: Offline deletes; anything else is stored as-is (an
 * unknown word is stored and simply draws nothing). */
export function applyPresenceFrame(
  presence: PresenceState,
  userId: number,
  status: UserStatus,
): void {
  if (status === "offline") {
    delete presence.map[userId];
  } else {
    presence.map[userId] = status;
  }
}

/** Wholesale replacement, never a merge — the list is sparse, and merging
 * leaves everybody who left while we were away lit forever. `enabled` is
 * deliberately NOT touched here: the capability is the state body's to set. */
export function applyPresenceSync(presence: PresenceState, rows: PresenceEntry[]): void {
  presence.map = {};
  for (const r of rows) {
    if (r.status !== "offline") presence.map[r.user_id] = r.status;
  }
}

/** From GET /v1/state: the key's presence IS the capability probe. */
export function applyPresenceState(presence: PresenceState, rows: PresenceEntry[] | null): void {
  presence.enabled = rows !== null;
  applyPresenceSync(presence, rows ?? []);
}

/** null for offline, unknown ids AND unnameable statuses — all three draw
 * nothing (a grey dot would assert "offline" about a newer state). */
export function statusOf(presence: PresenceState, userId: number): UserStatus | null {
  const s = presence.map[userId];
  if (s === undefined) return null;
  if (s !== "online" && s !== "afk" && s !== "dnd" && s !== "invisible" && s !== "in_call") {
    return null;
  }
  return s;
}

/** The channel to name in somebody's "In a call" status (D269), or null when
 * there is nothing to name.
 *
 * The server never says WHERE a call is — the status is one word — so the name
 * comes from the call rosters this client was already told about, which are
 * exactly the calls in channels it may see (`rooms::announce` addresses a
 * private channel's to its members, D114). A room we cannot see has no roster
 * here, and the status stays the bare "In a call": the mask working, not a
 * hole in it.
 *
 * A DM is never named — its name is a person's, and "In a call in «Olya»" is a
 * sentence about the wrong thing — so rooms are scanned for one that is not a
 * DM rather than the first match being rejected if it is one. */
export function callPlace(rooms: Room[], userId: number): string | null {
  const room = rooms.find((r) => !isDmRoom(r) && r.callMemberIds.includes(userId));
  return room && room.name !== "" ? room.name : null;
}

/** The word a status dot carries as its tooltip — one function for every
 * surface that draws one (the sidebar's rows, the profile card), so the four
 * of them cannot word one status differently. `place` is `callPlace`'s answer
 * and is read under "in_call" alone; every other status is one word with
 * nowhere to put it. */
export function presenceTitle(status: UserStatus, place: string | null, t: Dict): string {
  switch (status) {
    case "afk":
      return t.presenceAfk();
    case "dnd":
      return t.presenceDnd();
    case "invisible":
      return t.presenceInvisible();
    case "in_call":
      return place ? t.presenceInCallAt(place) : t.presenceInCall();
    default:
      return t.presenceOnline();
  }
}

/** Whose presence dot a row may draw (D194): DMs only, friends only, and the
 * call pill owns the corner when a call is live. */
export function dottedPeer(room: Room, friends: FriendsState): number | null {
  if (room.callMemberIds.length > 0) return null;
  if (!isDmRoom(room) || room.peerId === null) return null;
  return isFriend(friends, room.peerId) ? room.peerId : null;
}

// ---------------------------------------------------------------------------
// Calls (read-only, F3)

export function callsApply(calls: Record<number, Call>, call: Call): void {
  calls[call.channel_id] = call;
}

export function callsEnd(calls: Record<number, Call>, channelId: number): void {
  delete calls[channelId];
}

// ---------------------------------------------------------------------------
// Our own call (F4)

export function voiceConnecting(channelId: number): VoiceCall {
  return {
    channelId,
    phase: "connecting",
    muted: false,
    deafened: false,
    code: null,
    peers: [],
    speaking: [],
    rttMs: 0,
    lossPct: 0,
    concealmentPct: 0,
    volumes: {},
    share: null,
    audience: {},
  };
}

export function voicePeerUpsert(voice: VoiceCall, peer: VoicePeer): void {
  const i = voice.peers.findIndex((p) => p.userId === peer.userId);
  if (i >= 0) voice.peers[i] = peer;
  else voice.peers.push(peer);
}

export function voicePeerLeft(voice: VoiceCall, userId: number): void {
  voice.peers = voice.peers.filter((p) => p.userId !== userId);
  voice.speaking = voice.speaking.filter((id) => id !== userId);
}

export function voicePeerFlags(
  voice: VoiceCall,
  userId: number,
  muted: boolean,
  deafened: boolean,
): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) {
    p.muted = muted;
    p.deafened = deafened;
  }
}

export function voicePeerRtt(voice: VoiceCall, userId: number, rttMs: number): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) p.rttMs = rttMs;
}

export function voiceServerMute(voice: VoiceCall, userId: number, muted: boolean): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) p.serverMuted = muted;
}

export function voicePeerShare(
  voice: VoiceCall,
  userId: number,
  active: boolean,
  paused: boolean,
  hasAudio: boolean,
): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) {
    p.screenSharing = active;
    p.screenSharePaused = paused;
    p.screenShareHasAudio = hasAudio;
  }
  if (voice.share?.userId === userId) {
    if (!active) voice.share = null;
    else {
      voice.share.paused = paused;
      voice.share.hasAudio = hasAudio;
    }
  }
}
